import LegSection from '@/components/purchase/checkout/ancillaries/LegSection';
import AncillaryModal from '@/components/purchase/checkout/ancillaries/ancillary-modal/AncillaryModal';
import {
  getPurchaseFlowBookingAdditionalOffersSearch,
  refreshCheckoutBooking,
  removePurchaseFlowBookingAncillary,
} from '@/features/purchase/purchaseActions';
import { useBookingTripLegs } from '@/hooks/useBookingTripLegs';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import type { BookedAncillaryDTO, LegDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { GTM_EVENT, trackEcommerceEventV2 } from '@/utils/gtm';
import {
  mapBookedAncillaryToExtrasItem,
  roundMoney,
} from '@/utils/gtmPayloads';
import { Divider, Typography } from '@packages/webshop-design-system';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';

interface AncillaryListProps {}

const AncillaryList: FC<AncillaryListProps> = () => {
  const dispatch = useDispatch();
  const [selectedLeg, setSelectedLeg] = useState<LegDTO>();
  const booking = useCheckoutBooking();
  const { outboundLegs, inboundLegs } = useBookingTripLegs(booking);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const handlePurchaseFlowBookingAdditionalOffersSearch =
    useCallback(async () => {
      try {
        if (booking?.id) {
          await dispatch(
            getPurchaseFlowBookingAdditionalOffersSearch(booking.id)
          ).unwrap();
        }
      } catch {
        toast.error(<TransAlert i18nKey="failedToRetrieveAvailableExtras" />);
      }
    }, [booking?.id, dispatch]);

  useEffect(() => {
    handlePurchaseFlowBookingAdditionalOffersSearch();
  }, [handlePurchaseFlowBookingAdditionalOffersSearch]);

  const handleRemoveAncillary = useCallback(
    async (ancillary: BookedAncillaryDTO): Promise<void> => {
      if (!ancillary.id || !ancillary.bookedOfferId) {
        return;
      }

      await dispatch(
        removePurchaseFlowBookingAncillary({
          ancillaryId: ancillary.id,
          bookedOfferId: ancillary.bookedOfferId,
        })
      ).unwrap();

      // Tagging plan (BR-57983): pushed only after the BE removal succeeds.
      // price.amount is per unit, so value multiplies by quantity — same
      // semantics as the extras_added_to_booking push.
      const removedItem = mapBookedAncillaryToExtrasItem(ancillary);
      trackEcommerceEventV2(GTM_EVENT.EXTRAS_REMOVED_FROM_BOOKING, {
        currency: ancillary.price?.currency,
        value: roundMoney((removedItem.price ?? 0) * removedItem.quantity),
        total_passenger: booking?.passengers?.length,
        round_trip: (booking?.bookedTrips?.length ?? 0) > 1,
        extras_items: [removedItem],
      });

      await dispatch(refreshCheckoutBooking()).unwrap();
    },
    [booking, dispatch]
  );

  const renderLegWithChosenAncillaries = useCallback(
    (leg: LegDTO, index: number, array: Array<LegDTO>) => (
      <LegSection
        key={index}
        leg={leg}
        classNames={{
          accordionItem: cn({ 'border-none pb-0': index === array.length - 1 }),
        }}
        handleRemoveAncillary={handleRemoveAncillary}
        changeSelectedLeg={() => setSelectedLeg(leg)}
      />
    ),
    [handleRemoveAncillary]
  );

  const changeIsOverlayOpened = useCallback(
    (value: boolean) => {
      if (!value) {
        setSelectedLeg(undefined);
      }
    },
    [setSelectedLeg]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col laptop:flex-row laptop:gap-6">
        <div className="flex flex-1 flex-col">
          <Typography variant="body1-bold" as="h3" className="mb-3">
            <TransText i18nKey="outboundTrip" />
          </Typography>
          <div className="flex flex-col gap-5">
            {outboundLegs.map(renderLegWithChosenAncillaries)}
          </div>
        </div>
        {inboundLegs.length ? (
          <>
            <Divider
              className={cn('my-3 h-auto border-neutral-pale', {
                'mb-5 mt-1 max-w-[1px]': isLaptopOrBigger,
              })}
              orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
              variant="medium"
            />
            <div className="flex flex-1 flex-col">
              <Typography variant="body1-bold" as="h3" className="mb-3">
                <TransText i18nKey="returnTrip" />
              </Typography>
              <div className="flex flex-col gap-5">
                {inboundLegs.map(renderLegWithChosenAncillaries)}
              </div>
            </div>
          </>
        ) : null}
      </div>
      <AncillaryModal
        selectedLeg={selectedLeg}
        changeIsOverlayOpened={changeIsOverlayOpened}
      />
    </div>
  );
};

export default AncillaryList;
